import { CustomTheme, createStyles, makeStyles } from '@material-ui/core'
import Phone from 'Assets/Images/9.jpg'
import Survey from 'Assets/Images/survey.png'
import Button from 'Components/Base/Button'
import Checkbox from 'Components/Base/Checkbox'
import Column from 'Components/Base/Column'
import Icon from 'Components/Base/Icon'
import Row from 'Components/Base/Row'
import Typography from 'Components/Base/Typography'
import { StyledErrorText } from 'Styles/global'
import { sizes } from 'Styles/sizes'
import { useFormik } from 'formik'
import useDashboard from 'hooks/useDashboard'
import React, { useEffect, useState } from 'react'
import { Dropdown, Form } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { DashboardType } from 'types/Dashboards'
import * as Yup from 'yup'
import ContentCard from '../ContentCards/ContentCard'
import './TextSettingsCard.scss'

interface Props {
  className?: string
  maximumReading?: number
  highSystolic?: number
  highDiastolic?: number
  type: DashboardType
  onToggleModal?: (isOpen: boolean) => void
}

interface FormValues {
  maximumValue: number | undefined
  highSystolic: number | undefined
  highDiastolic: number | undefined
  type: number
}

export default function TextSettingsCard({ className, maximumReading, highSystolic, highDiastolic, type, onToggleModal }: Props) {
  const navigate = useNavigate()
  const { updateGlucoseConfig, updateBloodPressureConfig, reorderSupplies } = useDashboard()
  const [attemptedUpdate, setAttemptedUpdate] = useState<boolean>(false)
  const [success, setSuccess] = useState<boolean | undefined>()
  const [updating, setUpdating] = useState(false)
  const [strips, setStrips] = useState(false)
  const [lancets, setLancets] = useState(false)
  const [perDay, setPerDay] = useState(0)
  const [value, setValue] = useState('')
  const styles = useStyles()
  const theme = useTheme()

  const updateConfigValues = (values: FormValues) => {
    setUpdating(true)
    switch (type) {
      case DashboardType.Glucose:
        values.maximumValue &&
          updateGlucoseConfig(values.maximumValue)
            .then((res) => {
              if (res.status === 200) {
                setSuccess(true)
              }
            })
            .finally(() => setUpdating(false))
        break

      case DashboardType.BloodPressure:
        values.highSystolic &&
          values.highDiastolic &&
          updateBloodPressureConfig(values.highSystolic, values.highDiastolic)
            .then((res) => {
              if (res.status === 200) {
                setSuccess(true)
              }
            })
            .finally(() => setUpdating(false))
        break
    }
  }

  const initialValues: FormValues = {
    maximumValue: maximumReading,
    highSystolic: highSystolic,
    highDiastolic: highDiastolic,
    type: type,
  }

  const validationSchema = Yup.object().shape({
    maximumValue: Yup.number()
      .typeError('Must be a number')
      .when('type', {
        is: DashboardType.Glucose,
        then: Yup.number().required('Limit is required'),
      }),
    highSystolic: Yup.number().when('type', {
      is: DashboardType.BloodPressure,
      then: Yup.number().typeError('Must be a number').required('Systolic is required').max(180, 'Systolic max is 180'),
    }),
    highDiastolic: Yup.number().when('type', {
      is: DashboardType.BloodPressure,
      then: Yup.number().typeError('Must be a number').required('Diastolic is required').max(110, 'Diastolic max is 110'),
    }),
  })

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => updateConfigValues(values),
  })

  useEffect(() => {
    formik.setFieldValue('maximumValue', maximumReading)
    formik.setFieldValue('highSystolic', highSystolic)
    formik.setFieldValue('highDiastolic', highDiastolic)
    formik.setFieldValue('type', type)
  }, [maximumReading, highSystolic, highDiastolic, type])

  const openSurveySite = () => {
    window.open('https://www.surveymonkey.com/r/KDPKK92', '_blank')
  }

  const openAppStore = () => {
    window.open('https://tosto.re/triahealth', '_blank', 'noreferrer')
  }

  const navigateToResources = () => {
    window.scrollTo(0, 0)

    navigate('/resources/bloodpressure')
  }

  return (
    <StyledContainer>
      <StyledColumn>
        <StyledLeftContainer>
          <StyledHeaderRow>
            <Typography font="bold" size={26}>
              {`My Blood ${type === DashboardType.Glucose ? 'Glucose' : 'Pressure'} Text Settings`}
            </Typography>
          </StyledHeaderRow>
          {type === DashboardType.Glucose ? (
            <>
              <Row center>
                <Typography font="bold" color={theme.colors.black} size={20}>
                  Critical Low Reading Alert:
                </Typography>
              </Row>
              <Row center>
                <Typography font="regular" color={theme.colors.black} size={20}>
                  {`< 55 (mg/dL)`}
                </Typography>
              </Row>
              <hr style={{ height: 2, width: '85%', backgroundColor: 'black' }} />
              <Row center>
                <Typography font="bold" color={theme.colors.black} size={20}>
                  Low Reading Alert:
                </Typography>
              </Row>
              <Row center>
                <Typography font="regular" color={theme.colors.black} size={20}>
                  {`55-70 (mg/dL)`}
                </Typography>
              </Row>
              <hr style={{ height: 2, width: '85%', backgroundColor: 'black' }} />
              <Row center>
                <Typography font="bold" color={theme.colors.black} size={20}>
                  High Reading Alert:
                </Typography>
              </Row>
              <Row center styles={{ marginBottom: 25 }}>
                <Typography font="regular" color="neutral3" size={20}>
                  {`> 250 (mg/dL)`}
                </Typography>
              </Row>
            </>
          ) : (
            <>
              <Row center>
                <Typography font="bold" color={theme.colors.black} size={20}>
                  Low Reading Alert:
                </Typography>
              </Row>
              <Row center>
                <Typography font="regular" color={theme.colors.black} size={20}>
                  {`< 90 Systolic or < 50 Diastolic`}
                </Typography>
              </Row>
              <hr style={{ height: 2, width: '85%', backgroundColor: 'black' }} />
              <Row center>
                <Typography font="bold" color={theme.colors.black} size={20}>
                  High Reading Alert:
                </Typography>
              </Row>
              <Row center styles={{ marginBottom: 25 }}>
                <Typography font="regular" color="neutral3" size={20}>
                  {`> 180 Systolic or > 110 Diastolic`}
                </Typography>
              </Row>
            </>
          )}
        </StyledLeftContainer>
        <StyledCustomizeContainer>
          <Column center>
            <Typography font="bold" color={theme.colors.primary1} size={20} styles={{ textAlign: 'center' }}>
              Customize Your High Reading Alert:
            </Typography>
            <hr className={styles.divider} />
          </Column>
          {type === DashboardType.Glucose ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginBottom: 10,
              }}
            >
              <Form.Control
                onChange={(e) => formik.setFieldValue('maximumValue', e.currentTarget.value)}
                value={formik.values.maximumValue}
                style={{ width: '125px', marginRight: 10, marginBottom: 0 }}
                placeholder=""
                className="login-input"
              />
              <Column center>
                <Typography font="semi-bold" color={theme.colors.black} size={20}>
                  (mg/dL)
                </Typography>
              </Column>
            </div>
          ) : (
            <>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginBottom: 10,
                }}
              >
                <Form.Control
                  type={'number'}
                  onChange={(e) => formik.setFieldValue('highSystolic', e.currentTarget.value)}
                  value={formik.values.highSystolic}
                  style={{ width: '125px', marginRight: 10, marginBottom: 0 }}
                  placeholder=""
                  className="login-input"
                />
                <Column center>
                  <Typography font="semi-bold" color={theme.colors.black} size={20}>
                    /
                  </Typography>
                </Column>
                <Form.Control
                  type={'number'}
                  onChange={(e) => formik.setFieldValue('highDiastolic', e.currentTarget.value)}
                  value={formik.values.highDiastolic}
                  style={{ width: '125px', marginLeft: 10, marginBottom: 0 }}
                  placeholder=""
                  className="login-input"
                />
              </div>
              <Row center>
                <Typography font="bold" color={theme.colors.black} size={15}>
                  Maximum High Setting: 180/110
                </Typography>
              </Row>
            </>
          )}
          <div>
            {success === true && attemptedUpdate && (
              <Row center>
                <Typography font="bold" color={theme.colors.primary1} size={15}>
                  Settings Updated!
                </Typography>
              </Row>
            )}
            {success === false && attemptedUpdate && (
              <Row center>
                <Typography font="bold" color="tertiary1" size={15}>
                  Error updating settings
                </Typography>
              </Row>
            )}
            <Row center>
              {formik.errors.maximumValue && attemptedUpdate && (
                <StyledErrorText styles={{ textAlign: 'center', marginBottom: 10 }}>{formik.errors.maximumValue}</StyledErrorText>
              )}
            </Row>
            <Row center>
              {formik.errors.highSystolic && attemptedUpdate && (
                <StyledErrorText styles={{ textAlign: 'center', marginBottom: 10 }}>{formik.errors.highSystolic}</StyledErrorText>
              )}
            </Row>
            <Row center>
              {formik.errors.highDiastolic && attemptedUpdate && (
                <StyledErrorText styles={{ textAlign: 'center', marginBottom: 10 }}>{formik.errors.highDiastolic}</StyledErrorText>
              )}
            </Row>
          </div>
          <div className={styles.buttonRow}>
            <Button
              pageName="dashboardGlucose"
              className={styles.button}
              styles={{
                backgroundColor: '#00677F',
                width: 128,
              }}
              onClick={() => {
                setAttemptedUpdate(true)
                formik.handleSubmit()
              }}
              loading={updating}
              text={'Update'}
            />
            <Button
              pageName="dashboardGlucose"
              className={styles.button}
              styles={{
                backgroundColor: theme.colors.darkGray,
              }}
              onClick={() => {
                window.scrollTo(0, 0)
                navigate('/profile/access')
              }}
              text={'Add a Caregiver'}
            />
          </div>
        </StyledCustomizeContainer>
      </StyledColumn>
      <StyledRightColumn>
        {type === DashboardType.Glucose && (
          <StyledRightContainer>
            <StyledHeaderRow>
              <Typography font="bold" size={26}>
                {`Diabetes Supply Re-Order`}
              </Typography>
            </StyledHeaderRow>
            <Row center>
              <Typography size={20} styles={{ fontWeight: 600 }}>
                How often are you testing?
              </Typography>
            </Row>

            <Row center>
              <Dropdown>
                <Dropdown.Toggle
                  variant="secondary"
                  id="dropdown-basic"
                  className={styles.toolbarButton}
                  style={{ backgroundColor: '#F2F2F2', color: '#000', border: '1px solid #bdbdbd', width: 200 }}
                >
                  {value}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item
                    onClick={() => {
                      setValue('Once per day')
                      setPerDay(1)
                    }}
                  >
                    Once per day
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setValue('Twice per day')
                      setPerDay(2)
                    }}
                  >
                    Twice per day
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setValue('Three times per day')
                      setPerDay(3)
                    }}
                  >
                    Three times per day
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setValue('Four times per day')
                      setPerDay(4)
                    }}
                  >
                    Four times per day
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setValue('Five times per day')
                      setPerDay(5)
                    }}
                  >
                    Five times per day
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setValue('Six times per day')
                      setPerDay(6)
                    }}
                  >
                    Six times per day
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      setValue('Seven times per day')
                      setPerDay(7)
                    }}
                  >
                    Seven times per day
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Row>
            <Row center>
              <Typography size={20} styles={{ fontWeight: 600 }}>
                Please select the type of supplies you need:
              </Typography>
            </Row>
            <Row center styles={{ justifyContent: 'space-between' }}>
              <Row>
                <Checkbox checked={strips} onClick={() => setStrips(!strips)} />
                <Typography size={18} styles={{ fontWeight: 400, marginLeft: 20, marginRight: 20 }}>
                  Testing strips
                </Typography>
              </Row>
              <Row>
                <Checkbox checked={lancets} onClick={() => setLancets(!lancets)} />
                <Typography size={18} styles={{ fontWeight: 400, marginLeft: 20, marginRight: 20 }}>
                  Lancets
                </Typography>
              </Row>
            </Row>
            <div>
              {success === true && attemptedUpdate && (
                <Row center>
                  <Typography font="bold" color={theme.colors.primary1} size={15}>
                    Settings Updated!
                  </Typography>
                </Row>
              )}
              {success === false && attemptedUpdate && (
                <Row center>
                  <Typography font="bold" color="tertiary1" size={15}>
                    Error updating settings
                  </Typography>
                </Row>
              )}
              <Row center>
                {formik.errors.maximumValue && attemptedUpdate && (
                  <StyledErrorText styles={{ textAlign: 'center', marginBottom: 10 }}>{formik.errors.maximumValue}</StyledErrorText>
                )}
              </Row>
              <Row center>
                {formik.errors.highSystolic && attemptedUpdate && (
                  <StyledErrorText styles={{ textAlign: 'center', marginBottom: 10 }}>{formik.errors.highSystolic}</StyledErrorText>
                )}
              </Row>
              <Row center>
                {formik.errors.highDiastolic && attemptedUpdate && (
                  <StyledErrorText styles={{ textAlign: 'center', marginBottom: 10 }}>{formik.errors.highDiastolic}</StyledErrorText>
                )}
              </Row>
            </div>
            <div className={styles.buttonRow}>
              <Button
                pageName="dashboardGlucose"
                className={styles.button}
                styles={{
                  backgroundColor: '#00677F',
                }}
                onClick={() => {
                  reorderSupplies(perDay, strips, lancets).then(() => {
                    setValue('')
                    setPerDay(0)
                    setStrips(false)
                    setLancets(false)
                  })
                }}
                loading={updating}
                text={'Submit'}
              />
            </div>
          </StyledRightContainer>
        )}

        {type === DashboardType.Glucose && (
          <StyledContainer style={{ justifyContent: 'space-between', marginTop: 20 }}>
            <ContentCard
              backgroundImage={Survey}
              buttonText={'Provide Feedback'}
              buttonOnClick={openSurveySite}
              buttonLink={'https://www.surveymonkey.com/r/KDPKK92'}
            />
            <ContentCard backgroundImage={Phone} buttonText={'Download Mobile App'} buttonOnClick={openAppStore} />
          </StyledContainer>
        )}
        {type === DashboardType.BloodPressure && (
          <>
            <StyledContainer style={{ justifyContent: 'space-between', marginTop: 0 }}>
              <ContentCard backgroundImage={Phone} buttonText={'Download Mobile App'} buttonOnClick={openAppStore} />
              <ContentCard buttonOnClick={() => onToggleModal && onToggleModal(true)} buttonText={`What's New?`}>
                <StyledCardContent>
                  <div />
                  <div>
                    <Row center>
                      <Icon name="sparkles" color={theme.colors.primary1} size="6x" />
                    </Row>
                    <Row center>
                      <Typography
                        font="bold"
                        color={theme.colors.primary1}
                        size={24}
                        styles={{
                          lineHeight: '30px',
                          textAlign: 'center',
                          marginTop: 20,
                        }}
                      >
                        Dashboard
                      </Typography>
                    </Row>
                    <Row center>
                      <Typography
                        font="bold"
                        color={theme.colors.primary1}
                        size={24}
                        styles={{
                          lineHeight: '30px',
                          textAlign: 'center',
                        }}
                      >
                        Updates
                      </Typography>
                    </Row>
                  </div>
                  <div></div>
                </StyledCardContent>
              </ContentCard>
            </StyledContainer>
            <StyledContainer style={{ justifyContent: 'space-between', marginTop: 20 }}>
              <ContentCard buttonText={'Explore'} buttonOnClick={navigateToResources}>
                <StyledCardContent>
                  <div />
                  <div>
                    <Row center>
                      <Icon name={'heartbeat'} color={theme.colors.primary1} size={'8x'} />
                    </Row>
                    <Row center>
                      <Typography
                        font="bold"
                        color={theme.colors.primary1}
                        size={24}
                        styles={{
                          lineHeight: '30px',
                          textAlign: 'center',
                          marginTop: 20,
                        }}
                      >
                        {`Blood Pressure`}
                      </Typography>
                    </Row>
                    <Row center>
                      <Typography
                        font="bold"
                        color={theme.colors.primary1}
                        size={24}
                        styles={{
                          lineHeight: '30px',
                          textAlign: 'center',
                        }}
                      >
                        {`Resources`}
                      </Typography>
                    </Row>
                  </div>
                  <div></div>
                </StyledCardContent>
              </ContentCard>
              <ContentCard
                backgroundImage={Survey}
                buttonText={'Provide Feedback'}
                buttonOnClick={openSurveySite}
                buttonLink={'https://www.surveymonkey.com/r/KDPKK92'}
              />
            </StyledContainer>
          </>
        )}
      </StyledRightColumn>
    </StyledContainer>
  )
}

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media ${sizes.laptop} {
    flex-direction: row;
  }
`
const StyledLeftContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 50px;
  border: 1px solid #bdbdbd;
  height: 454px;
`
const StyledCustomizeContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 0px;
  margin-bottom: 50px;
  border: 1px solid #bdbdbd;
  height: fit-content;
`

const StyledColumn = styled(Column)`
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border: 1px solid #bdbdbd;
  width: 100%;
  border-radius: 7px;
  margin-right: auto;
  padding: 20px 15px;
  height: 703px;
  @media ${sizes.laptop} {
    width: 48%;
  }
`
const StyledRightContainer = styled(Column)`
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border: 1px solid #bdbdbd;
  width: 100%;
  border-radius: 7px;
  height: 394px;
  margin-left: auto;
  padding: 10px 20px 20px;
  justify-content: space-between;
  @media ${sizes.laptop} {
    width: 100%;
  }
`

const StyledRightColumn = styled(Column)`
  width: 100%;
  margin-top: 30px;
  @media ${sizes.laptop} {
    margin-top: 0px;
    width: 50%;
  }
`

const StyledHeaderRow = styled(Row)`
  background-color: ${({ theme }) => theme.colors.primary1};
  color: ${({ theme }) => theme.colors.white};
  height: 86px;
  align-items: center;
  justify-content: center;
  display: flex;
  margin-bottom: 10px;
`

const StyledCardContent = styled(Column)`
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  height: 100%;
  justify-content: space-between;
`

const useStyles = makeStyles<CustomTheme>((theme) =>
  createStyles({
    divider: {
      height: 2,
      width: '85%',
      backgroundColor: '#00677F',
    },
    column: {
      border: `1px solid #828282`,
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      paddingBottom: 20,
      marginBottom: 20,
    },
    buttonRow: {
      display: 'flex',
      marginTop: 10,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    button: {
      border: '0px',
      minWidth: '35%',
      marginRight: 5,
      marginLeft: 5,
      maxWidth: 168,
      paddingTop: 10,
      paddingBottom: 10,
      borderRadius: 0,
    },
  }),
)
